import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { formatClockTime, CLOCK_HUNDREDTHS_THRESHOLD_MS } from '@draughts/shared';

/**
 * Upward corrections smaller than this are latency jitter, not time genuinely
 * being handed back, so we absorb them and let the digits keep falling.
 *
 * Sized to the server's clock-sync period (1s, see server/game/clock-manager.ts):
 * anything shorter than a full missed sync is noise. A per-move increment never
 * reaches this branch — it lands on the player who just moved, whose clock has
 * already gone inactive, and every `isRunning` transition snaps unconditionally.
 */
const CLOCK_JITTER_SNAP_MS = 1000;

/**
 * Formats a clock and keeps it flowing between store updates.
 *
 * The store is the source of truth but only advances ~4x/second, while below
 * 10s the display shows hundredths — so reading it directly makes the digits
 * visibly skip. This interpolates at frame rate instead, so the clock never
 * stalls: not between store ticks, and not while a piece drag pauses them.
 *
 * Invariants worth preserving if you touch this:
 *
 * - **Absolute projection, never accumulated deltas.** Each frame recomputes
 *   `anchor - (now - anchoredAt)` from scratch. That is what makes tab
 *   backgrounding correct for free: rAF stops, `Date.now()` doesn't, so the
 *   first frame after resume lands on the right value with no catch-up pass.
 * - **Downward corrections are never clamped.** The display can only ever run
 *   ahead of the server by at most the jitter, and is pulled back the moment
 *   the server reports less.
 * - **Every `isRunning` transition snaps.** Turn switch, increment, game end,
 *   takeback and reconnect all fall out of that one rule, and it is also what
 *   stops the jitter clamp's error from accumulating across a game.
 * - `Date.now()` inside the loop, not rAF's `timestamp` argument: same time
 *   base as the stores' `lastSyncAt`, and no `performance.timeOrigin` skew
 *   under fake timers.
 * - No `prefers-reduced-motion` gate. Changing digits are information, not
 *   motion — and the test setup forces that query to match, which would
 *   freeze the clock across the whole suite.
 *
 * Trade-off, stated: holding back upward jumps while accepting downward ones
 * settles the display roughly one peak jitter (~100-150ms) *below* the true
 * value. That is the safe direction — it never shows time you don't have — it
 * is bounded rather than cumulative, and it resets at every turn switch.
 *
 * NOTE: the loop reschedules itself, so `vi.runAllTimers()` will spin forever
 * against a mounted running clock. Use `vi.advanceTimersByTime()` in tests.
 *
 * @param timeMs    authoritative remaining milliseconds, from the store
 * @param isRunning whether this is the clock currently counting down
 * @returns the formatted string to render
 */
export function useSmoothClock(timeMs: number, isRunning: boolean): string {
  const anchorRef = useRef({ valueMs: timeMs, atMs: Date.now() });
  const wasRunningRef = useRef(isRunning);
  const [text, setText] = useState(() => formatClockTime(timeMs));

  // Re-anchor whenever the store hands us a new value (or the clock starts or
  // stops). Runs before paint so the first render — and every assertion in a
  // test that never advances a frame — already shows the right string.
  useLayoutEffect(() => {
    const now = Date.now();
    const { valueMs, atMs } = anchorRef.current;
    const projected = Math.max(0, valueMs - (now - atMs));
    const delta = timeMs - projected;

    // Only clamp a clock that was already running before this update: a start,
    // a stop or a turn switch must take the store's value verbatim.
    const stillRunning = isRunning && wasRunningRef.current;
    const isJitter = stillRunning && delta > 0 && delta < CLOCK_JITTER_SNAP_MS;
    const next = isJitter ? projected : timeMs;

    anchorRef.current = { valueMs: next, atMs: now };
    wasRunningRef.current = isRunning;
    setText(formatClockTime(next));
  }, [timeMs, isRunning]);

  /*
   * A FOTOGRAMMI SOLO DOVE SERVE DAVVERO, cioe' sotto i dieci secondi.
   *
   * Qui c'era un `requestAnimationFrame` che si riprogrammava sempre, e il
   * commento diceva — giustamente — che sopra i dieci secondi la stringa non
   * cambia e `setText` con lo stesso valore e' un bail-out di React: nessun
   * render, nessun ridisegno. Il ragionamento era giusto e la conclusione no,
   * perche' saltava un passaggio: un rAF che si riprogramma TIENE VIVA LA
   * PIPELINE DI RENDERING a sessanta giri al secondo anche quando non produce
   * niente. Non e' il costo del JavaScript — quello e' qualche millisecondo —
   * e' il costo di non lasciare mai dormire il compositore.
   *
   * Misurato (`measure-idle.mjs --dove presenza --throttle 1`, partita ferma
   * con l'orologio in corsa): spegnere questo ciclo portava il renderer dal 14%
   * al 10% di un core e l'albero del browser dal 17% all'11%, mentre lo
   * `ScriptDuration` si muoveva appena — da 1424ms a 1319ms su 25 secondi. La
   * prova che il peso non era nel lavoro svolto ma nel fatto di svegliarsi.
   *
   * Quindi: sotto la soglia dei centesimi si resta a fotogrammi, perche' li' le
   * cifre cambiano davvero ogni frame; sopra, si DORME fino allo scoccare del
   * secondo, che e' l'unico istante in cui il testo puo' cambiare. Il risveglio
   * non e' mai in ritardo sul passaggio alla soglia, perche' si dorme al piu'
   * fino a quella.
   *
   * Gli invarianti della testata restano tutti: si proietta sempre in modo
   * ASSOLUTO dall'ancora (`anchor - (now - anchoredAt)`), mai accumulando
   * delta, quindi una scheda in secondo piano — dove sia rAF sia i timer
   * rallentano — torna sul valore giusto al primo risveglio, senza recuperi.
   */
  useEffect(() => {
    if (!isRunning) return;

    let frame = 0;
    let attesa = 0;

    const passo = () => {
      const { valueMs, atMs } = anchorRef.current;
      const restante = Math.max(0, valueMs - (Date.now() - atMs));
      setText(formatClockTime(restante));

      if (restante < CLOCK_HUNDREDTHS_THRESHOLD_MS) {
        frame = requestAnimationFrame(passo);
        return;
      }
      /* Il resto della divisione per mille e' quanto manca al prossimo scatto
         delle cifre; `|| 1000` copre il caso in cui si cada esatti sul secondo,
         dove il resto e' zero e dormire zero vorrebbe dire non dormire. */
      const alSecondo = restante % 1000 || 1000;
      attesa = window.setTimeout(passo, Math.min(alSecondo, restante - CLOCK_HUNDREDTHS_THRESHOLD_MS) || 1);
    };

    passo();
    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(attesa);
    };
  }, [isRunning]);

  return text;
}
